import React, { useCallback, useRef } from "react";
import classnames from "classnames";
import { CSSTransition } from "react-transition-group";

import styles from "./Accordion.module.scss";

export interface IAccordionProps {
    visible: boolean;
    maxHeight: number | string;
    className?: string;
    onChanged?: (visible: boolean) => void;
    children?: React.ReactNode;
    rememberHeight?: boolean;
    unmountOnInvisible?: boolean;
}

export function Accordion(props: IAccordionProps) {
    const {
        maxHeight,
        visible,
        className,
        children,
        onChanged,
        rememberHeight = false,
        unmountOnInvisible = true,
    } = props;

    const initialCssMaxHeight =
        typeof maxHeight === "string" ? maxHeight : `${maxHeight}px`;

    const measuredHeightRef = useRef<string | null>(null);
    const nodeRef = useRef<HTMLDivElement | null>(null);

    const handleExit = useCallback(() => {
        if (nodeRef.current) {
            const { height } = nodeRef.current.getBoundingClientRect();
            const measuredHeight = `${height}px`;
            measuredHeightRef.current = measuredHeight;
            nodeRef.current.style.maxHeight = measuredHeight;
        }
    }, [nodeRef, measuredHeightRef, initialCssMaxHeight]);

    const setHeight = useCallback(() => {
        if (nodeRef.current) {
            nodeRef.current.style.maxHeight = rememberHeight
                ? (measuredHeightRef.current ?? initialCssMaxHeight)
                : initialCssMaxHeight;
        }
    }, [nodeRef, initialCssMaxHeight, measuredHeightRef, rememberHeight]);

    const clearHeight = useCallback(() => {
        if (nodeRef.current) {
            nodeRef.current.style.maxHeight = "0";
        }
    }, [nodeRef]);

    const isVisible = useCallback(() => {
        if (nodeRef.current) {
            nodeRef.current.style.maxHeight = "";
        }
        onChanged?.(true);
    }, [onChanged, nodeRef]);

    const isInvisible = useCallback(() => {
        onChanged?.(false);
    }, [onChanged]);

    return (
        <CSSTransition
            nodeRef={nodeRef}
            in={visible}
            timeout={200}
            mountOnEnter={unmountOnInvisible}
            unmountOnExit={unmountOnInvisible}
            onEnter={clearHeight}
            onEntering={setHeight}
            onEntered={isVisible}
            onExit={handleExit}
            onExiting={clearHeight}
            onExited={isInvisible}
            classNames={{
                enter: styles.enter,
                enterActive: styles["enter-active"],
                enterDone: styles["enter-done"],
                exit: styles.exit,
                exitActive: styles["exit-active"],
                exitDone: styles["exit-done"],
            }}
        >
            {() => (
                <div
                    ref={nodeRef}
                    className={classnames(styles.accordion, className)}
                >
                    {children}
                </div>
            )}
        </CSSTransition>
    );
}
